import type { BadgeStyle } from '$lib/components/atoms/badge/types';
import type { ChipImageSize, ChipStyle } from '$lib/components/atoms/chip/types';
import type { MarkerStyle } from '$lib/components/map/markers/markerContent/types';
import type { Partner1PoiData, Partner2PoiData, PoiCategory, PoiData } from '$lib/components/pois/types';
import type { PoiCategoryTrackerName, PoiPartnerTrackerName } from '$lib/services/tracking/googleAnalytics/events';
import { isMapPageContext, isRouteDetailsPageContext } from '$lib/services/urls/routeIds';

const engieVianeoBranding = {
	id: 'engie-vianeo',
	name: 'Engie Vianeo',
	brandLogoUrl: 'https://bff.viamichelin.com/static/img/advertising/engie-vianeo/brand-logo_w160.webp',
	brandLogoWhiteUrl: 'https://bff.viamichelin.com/static/img/advertising/engie-vianeo/brand-logo-white_w160.webp',
	primaryColor: '#00AAFF',
} as const;

const mobilizeBranding = {
	id: 'mobilize',
	name: 'Mobilize',
	brandLogoBlackUrl: 'https://bff.viamichelin.com/static/img/advertising/mobilize/brand-logo-black_w160.webp',
	brandLogoWhiteUrl: 'https://bff.viamichelin.com/static/img/advertising/mobilize/brand-logo-white_w160.webp',
	brandLogoDatasheetUrl: 'https://bff.viamichelin.com/static/img/advertising/mobilize/brand-logo-datasheet_w160.webp',
} as const;

type SponsorId = typeof engieVianeoBranding.id;

export const sponsoredPoi: Record<SponsorId, SponsoredPoi> = {
	[engieVianeoBranding.id]: {
		// badge is used on poi cards
		badge: {
			isSquare: true,
			imgUrl: engieVianeoBranding.brandLogoUrl,
			style: {
				'background-color': 'var(--color-parking-weakest)',
			},
		},
		// chip is used on poi filters
		chip: {
			text: engieVianeoBranding.name,
			imgSize: 'lg',
			unselected: {
				imgUrl: engieVianeoBranding.brandLogoUrl,
			},
			selected: {
				imgUrl: engieVianeoBranding.brandLogoWhiteUrl,
				style: {
					'--bg-color': engieVianeoBranding.primaryColor,
					'--color': 'var(--color-neutral-weakest)',
					'--hover-bg-color': '#51C4FD',
				},
			},
			tracking: {
				type: 'pixel',
				pages: [
					{
						matchGuard: route => isRouteDetailsPageContext(route),
						img: timestamp => `<img id="engie-pixel-details" src="https://ad.doubleclick.net/ddm/ad/N445802.140884ORANGE.FR/B33444953.420127677;sz=1x1;ord=[${timestamp}];dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;dc_sdk_apis=[APIFRAMEWORKS];dc_omid_p=[OMIDPARTNER];gdpr=$%7BGDPR%7D;gdpr_consent=$%7BGDPR_CONSENT_755%7D;dc_tdv=1?" border=0 width=1 height=1 alt="Advertisement" />`,
						imgId: 'engie-pixel-details',
					},
					{
						matchGuard: route => isMapPageContext(route),
						img: timestamp => `<img id="engie-pixel-map" src="https://ad.doubleclick.net/ddm/ad/N445802.140884ORANGE.FR/B33444953.420126774;sz=1x1;ord=[${timestamp}];dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;dc_sdk_apis=[APIFRAMEWORKS];dc_omid_p=[OMIDPARTNER];gdpr=$%7BGDPR%7D;gdpr_consent=$%7BGDPR_CONSENT_755%7D;dc_tdv=1?" border=0 width=1 height=1 alt="Advertisement" />`,
						imgId: 'engie-pixel-map',
					},
				],

			},
		},
		marker: {
			imgUrl: engieVianeoBranding.brandLogoWhiteUrl,
			style: {
				'background-color': engieVianeoBranding.primaryColor,
				'border-color': '#0088CC',
			},
		},
		datasheet: {
			brandLogoUrl: engieVianeoBranding.brandLogoUrl,
		},
		tracking: {
			poiCategory: 'partner_engie',
			poiPartner: 'engie',
		},
	},
} as const;

type SponsoredPoiConfig = typeof sponsoredPoi[keyof typeof sponsoredPoi];

export const partner1SponsorId = engieVianeoBranding.id;
export const partner2SponsorId = mobilizeBranding.id;
export const partner1SponsorConfig = sponsoredPoi[partner1SponsorId];
export const partner2SponsorConfig: SponsoredPoiConfig | undefined = undefined;
export const partner1Branding = engieVianeoBranding;
export const partner2Branding = mobilizeBranding;

export function isAnyPoiPartner(poi: PoiData): poi is Partner1PoiData | Partner2PoiData {
	return poi.__typename === 'PartnerPOI' || poi.__typename === 'Partner2POI';
}

export function isAnyPoiCategoryPartner(poiCategory: PoiCategory): poiCategory is 'PARTNER' | 'PARTNER2' {
	return poiCategory === 'PARTNER' || poiCategory === 'PARTNER2';
}

export function getSponsoredPoiCategoryConfigByPoi(poi: PoiData): SponsoredPoiConfig | undefined {
	if (poi.__typename === 'PartnerPOI') {
		return partner1SponsorConfig;
	}
	if (poi.__typename === 'Partner2POI') {
		return partner2SponsorConfig;
	}
}

export function getSponsorConfigByPoiCategory(poiCategory: PoiCategory): SponsoredPoiConfig | undefined {
	if (poiCategory === 'PARTNER') {
		return partner1SponsorConfig;
	}
	if (poiCategory === 'PARTNER2') {
		return partner2SponsorConfig;
	}
}

export function getSponsorConfigByPoi(poi: PoiData): SponsoredPoiConfig | undefined {
	if (poi.__typename === 'PartnerPOI') {
		return partner1SponsorConfig;
	}
	if (poi.__typename === 'Partner2POI') {
		return partner2SponsorConfig;
	}
}

function getPixelImg(id: string) {
	return document.getElementById(id);
}

export function sponsoredPoiTracking(selected: boolean, route: { id: string | null }, trackingData?: SponsoredPoiTracking['tracking']) {
	if (!trackingData) return;

	for (const { matchGuard, img, imgId } of trackingData.pages) {
		if (matchGuard?.(route) && selected) {
			if (getPixelImg(imgId)) {
				getPixelImg(imgId)?.remove();
			}

			document.body.insertAdjacentHTML('beforeend', img(Date.now().toString()));
		} else if (getPixelImg(imgId) && !selected) {
			getPixelImg(imgId)?.remove();
		}
	}
}

interface SponsoredPoiPixel {
	type: 'pixel';
	pages: {
		img: (args: any) => string;
		imgId: string;
		matchGuard?: (route: { id: string | null }) => boolean;
	}[];
}
interface SponsoredPoiTracking {
	tracking?: SponsoredPoiPixel;
}

interface SponsoredPoi {
	badge: SponsoredBadge;
	chip: SponsoredChip & SponsoredPoiTracking;
	marker: SponsoredMarker;
	datasheet: SponsoredDatasheet;
	tracking: {
		poiCategory: Extract<PoiCategoryTrackerName, 'partner_engie' | 'partner_mobilize'>;
		poiPartner: Extract<PoiPartnerTrackerName, 'engie' | 'mobilize'>;
	};
}

interface SponsoredBadge {
	isSquare: boolean;
	imgUrl: string;
	style: BadgeStyle;
}

interface SponsoredChip {
	text: string;
	imgSize?: ChipImageSize;
	unselected: {
		imgUrl?: string;
	};
	selected: {
		imgUrl?: string;
		style: ChipStyle;
	};
}

interface SponsoredMarker {
	imgUrl: string;
	imgSize?: 'sm' | 'md';
	style: MarkerStyle;
}

interface SponsoredDatasheet {
	brandLogoUrl: string;
}
